import { useState, useEffect, useCallback, memo } from 'react';
import { Av, Bdg, AiScoreRing, PH } from '../components/shared';
import { useApp } from '../context/AppContext';
import { getDashboardAiStats } from '../services/api';

export const PageAI = memo(()=>{
  const { showToast } = useApp();
  const [data, setData] = useState(null);

  useEffect(()=>{
    getDashboardAiStats().then(setData).catch(()=>{});
  },[]);

  const leads = data?.leads || [];

  return (
    <div className="pg fi">
      <PH eyebrow="Intelligence" title="AI Command Center" sub="Powered by Gotaps AI · Real-time analysis" live/>
      <div className="g4">
        {[
          {lbl:"Total Leads",val:String(data?.totalLeads??0),ico:"🎯",col:"#18181b",bg:"rgba(15,15,30,.08)",note:"All captured"},
          {lbl:"Scored Leads",val:String(data?.scoredLeads??0),ico:"✦",col:"#059669",bg:"rgba(5,150,105,.1)",note:"AI processed"},
          {lbl:"Hot Leads",val:String(data?.hotLeads??0),ico:"🔥",col:"#d97706",bg:"rgba(217,119,6,.1)",note:"Score ≥ 80"},
          {lbl:"Avg AI Score",val:String(data?.avgScore??0),ico:"📊",col:"#64748b",bg:"rgba(100,116,139,.1)",note:"Across all leads"},
        ].map(({lbl,val,ico,col,bg,note},i)=>(
          <div key={lbl} className={`card sc fi s${i+1}`} style={{"--sc":col}}><div className="sc-orb"/><div className="sc-ico" style={{background:bg}}>{ico}</div><div className="sc-l">{lbl}</div><div className="sc-v">{val}</div><div className="sc-tr" style={{color:"var(--t3)"}}>{note}</div></div>
        ))}
      </div>
      <div className="card-s fi s3">
        <div className="ch"><div><div className="ct">AI Lead Scoring</div><div className="cs">All leads ranked by AI model</div></div><span className="ai-chip">✦ Auto-scored</span></div>
        <table className="tbl"><thead><tr><th>Lead</th><th>Company</th><th>AI Score</th><th>Source</th><th>Status</th></tr></thead>
          <tbody>{leads.length > 0 ? leads.map(l=>(
            <tr key={l.id}>
              <td style={{fontWeight:560}}>{l.name||'—'}</td>
              <td>{l.company||'—'}</td>
              <td><div style={{display:"flex",alignItems:"center",gap:8}}><AiScoreRing score={l.score}/>{l.score>=80&&<span className="badge b-purple" style={{fontSize:9}}>🔥 Hot</span>}</div></td>
              <td><span className="badge b-blue">{l.source}</span></td>
              <td><Bdg s={l.status||'new'}/></td>
            </tr>
          )) : <tr><td colSpan="5" style={{textAlign:'center',color:'var(--t3)',padding:30}}>No scored leads yet</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════
   MEMBER ANALYTICS PANEL
═══════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════
   MEMBER ANALYTICS — Full Page (light theme)
══════════════════════════════════════════════════════ */
export const MemberAnalyticsPanel = memo(({member, onClose}) => {
  const [tab, setTab] = useState("overview");
  const base  = member.score || 70;
  const leads = member.leads || 0;
  const taps  = Math.round(base * 14.2 + leads * 3);
  const views = Math.round(taps * 2.7);
  const conns = Math.round(leads * 1.4);
  const saves = Math.round(leads * 0.8);
  const ctr   = Math.round((taps  / Math.max(views,1)) * 100);
  const conv  = Math.round((leads / Math.max(conns,1)) * 100);

  const week = [
    {d:"Mon",t:Math.round(taps*.12),v:Math.round(views*.11),l:Math.round(leads*.10)},
    {d:"Tue",t:Math.round(taps*.18),v:Math.round(views*.17),l:Math.round(leads*.18)},
    {d:"Wed",t:Math.round(taps*.14),v:Math.round(views*.15),l:Math.round(leads*.14)},
    {d:"Thu",t:Math.round(taps*.22),v:Math.round(views*.20),l:Math.round(leads*.24)},
    {d:"Fri",t:Math.round(taps*.19),v:Math.round(views*.19),l:Math.round(leads*.19)},
    {d:"Sat",t:Math.round(taps*.08),v:Math.round(views*.09),l:Math.round(leads*.08)},
    {d:"Sun",t:Math.round(taps*.07),v:Math.round(views*.09),l:Math.round(leads*.07)},
  ];
  const maxBar = Math.max(...week.map(d=>Math.max(d.t,d.v,d.l)),1);
  const accentColor = (member.grad || "").match(/#[a-f0-9]{6}/gi)?.[0] || "#0891b2";

  const KPIS = [
    {k:"Total Taps",    v:taps.toLocaleString(),  sub:"+12% vs last month",ico:"📲",c:"#0891b2"},
    {k:"Profile Views", v:views.toLocaleString(), sub:"+8% vs last month", ico:"👁", c:"#7c3aed"},
    {k:"Connections",   v:conns.toLocaleString(), sub:"+21% vs last month",ico:"🤝",c:"#059669"},
    {k:"Saves",         v:saves.toLocaleString(), sub:"+5% vs last month", ico:"🔖",c:"#d97706"},
    {k:"Leads",         v:leads.toLocaleString(), sub:"+17% vs last month",ico:"🎯",c:"#dc2626"},
    {k:"Engagement",    v:Math.round((conns/Math.max(views,1))*100)+"%", sub:"of viewers connected",ico:"✦",c:"#6366f1"},
  ];

  const SOURCES = [
    {s:"NFC Tap",    p:52, c:"#0891b2"},
    {s:"QR Scan",    p:28, c:"#7c3aed"},
    {s:"Email Link", p:13, c:"#059669"},
    {s:"Direct",     p:7,  c:"#d97706"},
  ];

  const TABS = [
    {id:"overview",ico:"⬡", lbl:"Overview"},
    {id:"activity",ico:"📈",lbl:"Activity"},
    {id:"sources", ico:"🔀",lbl:"Sources"},
  ];

  /* shared card style */
  const cardSt = {
    background:"rgba(255,255,255,.72)",
    border:"1px solid rgba(255,255,255,.9)",
    borderRadius:18,
    boxShadow:"0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)",
  };

  return (
    <div style={{
      position:"fixed",inset:0,zIndex:500,
      background:"#e6eaf4",
      display:"flex",flexDirection:"column",
      fontFamily:"var(--f)",
      animation:"pageFadeIn .2s ease",
    }}>
      <style>{`@keyframes pageFadeIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}`}</style>

      {/* ── TOP NAV ── */}
      <div style={{
        display:"flex",alignItems:"center",
        background:"rgba(255,255,255,.72)",
        backdropFilter:"blur(40px)",
        borderBottom:"1px solid rgba(255,255,255,.85)",
        boxShadow:"0 2px 16px rgba(0,0,0,.06)",
        flexShrink:0,position:"sticky",top:0,zIndex:10,
      }}>
        {/* Back */}
        <button onClick={onClose} style={{
          display:"flex",alignItems:"center",gap:8,
          padding:"16px 22px",border:"none",
          background:"transparent",cursor:"pointer",
          color:"#64748b",fontSize:13,fontFamily:"var(--f)",
          borderRight:"1px solid rgba(0,0,0,.06)",
          transition:"all .15s",flexShrink:0,
        }}
          onMouseEnter={e=>e.currentTarget.style.color="#0f172a"}
          onMouseLeave={e=>e.currentTarget.style.color="#64748b"}>
          <span style={{fontSize:16}}>←</span> Members
        </button>

        {/* Member identity */}
        <div style={{display:"flex",alignItems:"center",gap:13,padding:"10px 22px",flex:1}}>
          {member.photo ? (
            <img src={member.photo} alt={member.name} style={{width:38,height:38,borderRadius:11,objectFit:"cover",flexShrink:0}}/>
          ) : (
            <div style={{
              width:38,height:38,borderRadius:11,background:member.grad||"linear-gradient(150deg,#64748b,#059669)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontWeight:800,fontSize:13,color:"#fff",flexShrink:0,
              boxShadow:"0 3px 10px rgba(0,0,0,.18)",
            }}>{member.name ? member.name.slice(0,2).toUpperCase() : "??"}</div>
          )}
          <div>
            <div style={{fontSize:14,fontWeight:700,color:"#0f172a",letterSpacing:"-.2px"}}>{member.name}</div>
            <div style={{fontSize:11,color:"#94a3b8",marginTop:1}}>{member.job_title||member.position||member.role||"—"}</div>
          </div>
          <div style={{width:8,height:8,borderRadius:"50%",background:member.active?"#22c55e":"#94a3b8",boxShadow:member.active?"0 0 6px #22c55e":"none"}}/>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",alignSelf:"stretch"}}>
          {TABS.map(({id,ico,lbl})=>(
            <button key={id} onClick={()=>setTab(id)} style={{
              display:"flex",alignItems:"center",gap:7,
              padding:"0 22px",border:"none",
              background:tab===id?"rgba(255,255,255,.95)":"transparent",
              cursor:"pointer",fontSize:12.5,fontWeight:tab===id?700:500,
              fontFamily:"var(--f)",
              color:tab===id?"#0f172a":"#94a3b8",
              borderBottom:tab===id?`2.5px solid ${accentColor}`:"2.5px solid transparent",
              transition:"all .18s",
            }}
              onMouseEnter={e=>{if(tab!==id)e.currentTarget.style.color="#475569";}}
              onMouseLeave={e=>{if(tab!==id)e.currentTarget.style.color="#94a3b8";}}>
              <span style={{fontSize:14}}>{ico}</span>{lbl}
            </button>
          ))}
        </div>

        {/* AI badge */}
        <div style={{
          padding:"5px 15px",borderRadius:20,marginRight:22,flexShrink:0,
          background:"rgba(124,58,237,.08)",border:"1px solid rgba(124,58,237,.18)",
          color:"#7c3aed",fontSize:11,fontWeight:700,
        }}>✦ AI Score {member.score}</div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{flex:1,overflowY:"auto",padding:"28px 36px"}}>

        {/* ══ OVERVIEW ══ */}
        {tab==="overview"&&(
          <div style={{maxWidth:1060,margin:"0 auto"}}>
            {/* KPI strip */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:14,marginBottom:28}}>
              {KPIS.map(({k,v,sub,ico,c},i)=>(
                <div key={k} style={{...cardSt,padding:"20px 16px",position:"relative",overflow:"hidden",cursor:"default",transition:"all .2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=`0 8px 28px rgba(0,0,0,.1),0 0 0 2px ${c}22`;}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)";}}>
                  <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${c},${c}44)`,borderRadius:"18px 18px 0 0"}}/>
                  <div style={{width:36,height:36,borderRadius:10,background:`${c}14`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,marginBottom:14}}>{ico}</div>
                  <div style={{fontSize:26,fontWeight:800,color:"#0f172a",letterSpacing:"-1.5px",lineHeight:1}}>{v}</div>
                  <div style={{fontSize:11,color:c,marginTop:7,fontWeight:660}}>{k}</div>
                  <div style={{fontSize:10,color:"#94a3b8",marginTop:3}}>{sub}</div>
                </div>
              ))}
            </div>

            {/* Two-column */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:22}}>
              {/* Funnel */}
              <div style={{...cardSt,padding:"26px 28px"}}>
                <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:22}}>Conversion Funnel</div>
                {[
                  {lbl:"Profile Views",  val:views, pct:100, c:"#0891b2"},
                  {lbl:"Taps",           val:taps,  pct:Math.round(taps/Math.max(views,1)*100), c:"#7c3aed"},
                  {lbl:"Connections",    val:conns, pct:Math.round(conns/Math.max(views,1)*100), c:"#059669"},
                  {lbl:"Leads Captured", val:leads, pct:Math.round(leads/Math.max(views,1)*100), c:"#dc2626"},
                ].map(({lbl,val,pct,c})=>(
                  <div key={lbl} style={{marginBottom:18}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:7,alignItems:"center"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:7,height:7,borderRadius:"50%",background:c}}/>
                        <span style={{fontSize:12.5,color:"#475569",fontWeight:560}}>{lbl}</span>
                      </div>
                      <div style={{display:"flex",gap:10,alignItems:"center"}}>
                        <span style={{fontSize:11,color:"#94a3b8"}}>{val.toLocaleString()}</span>
                        <span style={{fontSize:11.5,fontWeight:700,color:c,minWidth:36,textAlign:"right"}}>{pct}%</span>
                      </div>
                    </div>
                    <div style={{height:8,borderRadius:4,background:"rgba(0,0,0,.06)",overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${c},${c}88)`,borderRadius:4,transition:"width 1.2s cubic-bezier(.16,1,.3,1)"}}/>
                    </div>
                  </div>
                ))}
              </div>

              {/* Engagement metrics 2x2 */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,alignContent:"start"}}>
                {[
                  {lbl:"Tap-Through Rate",val:ctr+"%",      c:"#0891b2",sub:"Taps ÷ Views",        ico:"📲"},
                  {lbl:"Conversion Rate", val:conv+"%",      c:"#059669",sub:"Leads ÷ Connections", ico:"🔄"},
                  {lbl:"Leads / Week",    val:Math.round(leads/4.3), c:"#d97706",sub:"30-day avg",  ico:"📅"},
                  {lbl:"Save Rate",       val:Math.round(saves/Math.max(views,1)*100)+"%",c:"#7c3aed",sub:"Saves ÷ Views",ico:"🔖"},
                ].map(({lbl,val,c,sub,ico})=>(
                  <div key={lbl} style={{...cardSt,padding:"20px 18px"}}>
                    <div style={{fontSize:20,marginBottom:10}}>{ico}</div>
                    <div style={{fontSize:28,fontWeight:800,color:c,letterSpacing:"-1.5px",lineHeight:1}}>{val}</div>
                    <div style={{fontSize:11.5,color:"#475569",marginTop:8,fontWeight:600}}>{lbl}</div>
                    <div style={{fontSize:10,color:"#94a3b8",marginTop:3}}>{sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Score summary card */}
            <div style={{...cardSt,padding:"28px 32px",display:"flex",alignItems:"center",gap:32}}>
              <svg width="110" height="110" viewBox="0 0 110 110" style={{flexShrink:0}}>
                <circle cx="55" cy="55" r="46" fill="none" stroke="rgba(0,0,0,.06)" strokeWidth="9"/>
                <circle cx="55" cy="55" r="46" fill="none" stroke={`url(#sg${member.id})`} strokeWidth="9"
                  strokeLinecap="round"
                  strokeDasharray={`${(member.score/100)*289} 289`}
                  transform="rotate(-90 55 55)"
                  style={{transition:"stroke-dasharray 1.4s cubic-bezier(.16,1,.3,1)"}}/>
                <defs>
                  <linearGradient id={`sg${member.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={accentColor}/>
                    <stop offset="100%" stopColor="#7c3aed"/>
                  </linearGradient>
                </defs>
                <text x="55" y="50" textAnchor="middle" fill="#0f172a" fontSize="24" fontWeight="800" fontFamily="var(--f)">{member.score}</text>
                <text x="55" y="65" textAnchor="middle" fill="#94a3b8" fontSize="9.5" fontFamily="var(--f)">AI SCORE</text>
              </svg>
              <div style={{flex:1}}>
                <div style={{fontSize:18,fontWeight:750,color:"#0f172a",marginBottom:7,letterSpacing:"-.3px"}}>{member.name}'s Performance Summary</div>
                <div style={{fontSize:13,color:"#64748b",lineHeight:1.75,maxWidth:500}}>
                  This member generated <b style={{color:"#dc2626"}}>{leads} leads</b> and received <b style={{color:"#0891b2"}}>{views.toLocaleString()} profile views</b> in 30 days, with a <b style={{color:"#059669"}}>{ctr}% tap-through rate</b> and <b style={{color:"#7c3aed"}}>{conv}% conversion rate</b>.
                </div>
                <div style={{display:"flex",gap:9,marginTop:16}}>
                  <span style={{padding:"5px 14px",borderRadius:20,background:"rgba(5,150,105,.1)",color:"#059669",fontSize:11,fontWeight:660,border:"1px solid rgba(5,150,105,.2)"}}>
                    {member.score>=90?"🏆 Top Performer":member.score>=70?"⭐ Good Standing":"📈 Growing"}
                  </span>
                  <span style={{padding:"5px 14px",borderRadius:20,background:"rgba(8,145,178,.08)",color:"#0891b2",fontSize:11,fontWeight:660,border:"1px solid rgba(8,145,178,.18)"}}>
                    {member.online?"● Online now":"○ Last seen today"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ ACTIVITY ══ */}
        {tab==="activity"&&(
          <div style={{maxWidth:1060,margin:"0 auto",display:"grid",gridTemplateColumns:"2fr 1fr",gap:22}}>
            {/* Bar chart */}
            <div style={{...cardSt,padding:"28px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:"#0f172a",letterSpacing:"-.2px"}}>Weekly Activity</div>
                  <div style={{fontSize:11.5,color:"#94a3b8",marginTop:3}}>Last 7 days — Taps, Views, Leads</div>
                </div>
                <div style={{display:"flex",gap:14}}>
                  {[["#0891b2","Taps"],["#7c3aed","Views"],["#059669","Leads"]].map(([c,l])=>(
                    <div key={l} style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:10,height:10,borderRadius:3,background:c}}/>
                      <span style={{fontSize:11,color:"#94a3b8",fontWeight:550}}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{display:"flex",gap:10,height:180,alignItems:"flex-end",padding:"0 4px"}}>
                {week.map((d,i)=>(
                  <div key={d.d} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6,height:"100%",justifyContent:"flex-end"}}>
                    <div style={{width:"100%",display:"flex",gap:2,alignItems:"flex-end",height:"calc(100% - 20px)"}}>
                      {[{v:d.t,c:"#0891b2"},{v:d.v,c:"#7c3aed"},{v:d.l,c:"#059669"}].map(({v,c},j)=>(
                        <div key={j} style={{
                          flex:1,
                          height:`${Math.max((v/maxBar)*100,2)}%`,
                          background:`linear-gradient(180deg,${c},${c}66)`,
                          borderRadius:"4px 4px 0 0",
                          boxShadow:`0 -2px 8px ${c}33`,
                          transition:`height 1.1s cubic-bezier(.16,1,.3,1) ${(i*.06)+(j*.02)}s`,
                        }}/>
                      ))}
                    </div>
                    <div style={{fontSize:10.5,color:"#94a3b8",fontWeight:600}}>{d.d}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:10,marginTop:16,paddingTop:14,borderTop:"1px solid rgba(0,0,0,.06)"}}>
                {week.map(d=>(
                  <div key={d.d} style={{flex:1,textAlign:"center",padding:"8px 4px",borderRadius:9,background:"rgba(0,0,0,.03)"}}>
                    <div style={{fontSize:12.5,fontWeight:760,color:"#0f172a"}}>{d.t+d.v+d.l}</div>
                    <div style={{fontSize:9,color:"#94a3b8",marginTop:2}}>{d.d}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity feed */}
            <div style={{...cardSt,padding:"28px"}}>
              <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:18}}>Recent Activity</div>
              {[
                {ico:"📲",txt:"NFC tap at TechConf 2026",       sub:"2 hours ago", c:"#0891b2"},
                {ico:"🤝",txt:"New connection — Bruce Wayne",    sub:"4 hours ago", c:"#059669"},
                {ico:"🎯",txt:"Lead captured — Diana Prince",    sub:"6 hours ago", c:"#dc2626"},
                {ico:"👁", txt:"Profile viewed 14 times",         sub:"Yesterday",   c:"#7c3aed"},
                {ico:"🔖",txt:"Card saved by 3 people",          sub:"2 days ago",  c:"#d97706"},
                {ico:"📲",txt:"QR scan at Networking Event",     sub:"3 days ago",  c:"#0891b2"},
                {ico:"🤝",txt:"New connection — Tony Stark",     sub:"4 days ago",  c:"#059669"},
              ].map((ev,i)=>(
                <div key={i} style={{display:"flex",gap:11,padding:"10px 0",borderBottom:i<6?"1px solid rgba(0,0,0,.05)":"none"}}>
                  <div style={{width:34,height:34,borderRadius:9,background:`${ev.c}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{ev.ico}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ev.txt}</div>
                    <div style={{fontSize:10.5,color:"#94a3b8",marginTop:2}}>{ev.sub}</div>
                  </div>
                  <div style={{width:6,height:6,borderRadius:"50%",background:ev.c,marginTop:6,flexShrink:0}}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ SOURCES ══ */}
        {tab==="sources"&&(
          <div style={{maxWidth:1060,margin:"0 auto",display:"grid",gridTemplateColumns:"1fr 1fr",gap:22}}>
            {/* Source bars */}
            <div style={{...cardSt,padding:"28px"}}>
              <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:24}}>Traffic Sources</div>
              {SOURCES.map(({s,p,c},i)=>(
                <div key={s} style={{marginBottom:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:9}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:9,height:9,borderRadius:2,background:c}}/>
                      <span style={{fontSize:13,fontWeight:650,color:"#334155"}}>{s}</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <span style={{fontSize:11,color:"#94a3b8"}}>{Math.round(taps*p/100).toLocaleString()} taps</span>
                      <span style={{fontSize:20,fontWeight:800,color:c}}>{p}%</span>
                    </div>
                  </div>
                  <div style={{height:10,borderRadius:5,background:"rgba(0,0,0,.06)",overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${p}%`,background:`linear-gradient(90deg,${c},${c}88)`,borderRadius:5,transition:`width 1.4s cubic-bezier(.16,1,.3,1) ${i*.12}s`}}/>
                  </div>
                </div>
              ))}
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Best day */}
              <div style={{...cardSt,padding:"26px"}}>
                <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:16}}>Top Performing Day</div>
                <div style={{display:"flex",alignItems:"center",gap:16}}>
                  <div style={{width:54,height:54,borderRadius:15,background:"linear-gradient(135deg,#0891b2,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0,boxShadow:"0 6px 20px rgba(8,145,178,.3)"}}>📅</div>
                  <div>
                    <div style={{fontSize:22,fontWeight:800,color:"#0f172a",letterSpacing:"-.5px"}}>Thursday</div>
                    <div style={{fontSize:12,color:"#94a3b8",marginTop:3}}>{(week[3].t+week[3].v+week[3].l).toLocaleString()} interactions · {week[3].l} leads</div>
                  </div>
                  <div style={{marginLeft:"auto",padding:"5px 13px",borderRadius:20,background:"rgba(5,150,105,.1)",border:"1px solid rgba(5,150,105,.2)"}}>
                    <div style={{fontSize:13,fontWeight:760,color:"#059669"}}>↑ 22%</div>
                    <div style={{fontSize:9.5,color:"#059669",opacity:.7,textAlign:"center"}}>above avg</div>
                  </div>
                </div>
              </div>

              {/* Source quality grid */}
              <div style={{...cardSt,padding:"26px",flex:1}}>
                <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:16}}>Source Quality</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
                  {[
                    {lbl:"Highest CTR",  val:"NFC Tap",    pct:"68%",   c:"#0891b2"},
                    {lbl:"Most Volume",  val:"NFC Tap",    pct:"52%",   c:"#7c3aed"},
                    {lbl:"Best Conv.",   val:"Email Link", pct:"31%",   c:"#059669"},
                    {lbl:"Fastest Lead", val:"QR Scan",    pct:"4.2min",c:"#d97706"},
                  ].map(({lbl,val,pct,c})=>(
                    <div key={lbl} style={{padding:"14px",borderRadius:12,background:`${c}08`,border:`1px solid ${c}18`}}>
                      <div style={{fontSize:10,color:"#94a3b8",fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",marginBottom:6}}>{lbl}</div>
                      <div style={{fontSize:13.5,fontWeight:700,color:"#0f172a"}}>{val}</div>
                      <div style={{fontSize:13,fontWeight:700,color:c,marginTop:3}}>{pct}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

/* ══════════════════════════════════════════════════════
   MEMBER LEADS — Full Page (light theme)
══════════════════════════════════════════════════════ */
export const MemberLeadsPanel = memo(({member, onClose}) => {
  const ALL_LEADS = [
    {id:101,name:"Chris Evans",    email:"c.evans@novatech.io",    phone:"+1 212 555 0192",country:"United States",city:"New York",   date:"Mar 4", aiScore:96,status:"new",      company:"NovaTech Inc."},
    {id:102,name:"Diana Prince",   email:"diana@apex.solutions",   phone:"+1 310 555 0183",country:"United States",city:"Los Angeles",date:"Mar 4", aiScore:81,status:"contacted",company:"Apex Solutions"},
    {id:103,name:"Bruce Wayne",    email:"b.wayne@wayne-ent.com",  phone:"+1 212 555 0741",country:"United States",city:"Gotham",     date:"Mar 3", aiScore:99,status:"qualified",company:"Wayne Ent."},
    {id:104,name:"Clark Kent",     email:"c.kent@dailyplanet.com", phone:"+1 785 555 0164",country:"United States",city:"Metropolis", date:"Mar 3", aiScore:58,status:"new",      company:"Daily Planet"},
    {id:105,name:"Natasha R.",     email:"n.r@redlabs.io",         phone:"+1 617 555 0299",country:"United States",city:"Boston",     date:"Mar 2", aiScore:91,status:"closed",   company:"Red Labs"},
    {id:106,name:"Tony Stark",     email:"tony@stark-ind.com",     phone:"+1 212 555 0039",country:"United States",city:"Malibu",     date:"Mar 2", aiScore:99,status:"qualified",company:"Stark Ind."},
    {id:107,name:"Peter Parker",   email:"p.parker@oscorp.net",    phone:"+1 646 555 0178",country:"United States",city:"Queens",     date:"Mar 1", aiScore:74,status:"contacted",company:"Oscorp"},
    {id:108,name:"Wanda Maximoff", email:"w.m@shield.gov",         phone:"+1 305 555 0144",country:"United States",city:"Westview",   date:"Feb 28",aiScore:88,status:"new",      company:"S.H.I.E.L.D."},
    {id:109,name:"Steve Rogers",   email:"s.r@shield.gov",         phone:"+1 202 555 0110",country:"United States",city:"Brooklyn",   date:"Feb 27",aiScore:95,status:"qualified",company:"S.H.I.E.L.D."},
  ];
  const memberLeads = ALL_LEADS.filter((_,i)=>(i+member.id)%3!==0).slice(0,member.leads||3);

  const [search,   setSearch]   = useState("");
  const [filterSt, setFilterSt] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const SC  = {new:"#0891b2",contacted:"#d97706",qualified:"#059669",closed:"#64748b"};
  const SBG = {new:"rgba(8,145,178,.1)",contacted:"rgba(217,119,6,.1)",qualified:"rgba(5,150,105,.1)",closed:"rgba(100,116,139,.1)"};

  const filtered = memberLeads.filter(l=>{
    const ms = l.name.toLowerCase().includes(search.toLowerCase()) ||
               l.email.toLowerCase().includes(search.toLowerCase()) ||
               l.company.toLowerCase().includes(search.toLowerCase());
    return ms && (filterSt==="all"||l.status===filterSt);
  });

  const totalQual = memberLeads.filter(l=>l.status==="qualified").length;
  const avgScore  = Math.round(memberLeads.reduce((a,l)=>a+l.aiScore,0)/Math.max(memberLeads.length,1));

  const downloadCSV = () => {
    const h = ["Name","Email","Phone","Company","Country","City","Date","AI Score","Status"];
    const csv = [h,...filtered.map(l=>[l.name,l.email,l.phone,l.company,l.country,l.city,l.date,l.aiScore,l.status])]
      .map(r=>r.map(v=>`"${v||""}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download = `leads_${member.name.replace(/\s+/g,"_")}.csv`;
    a.click();
  };

  const cardSt = {
    background:"rgba(255,255,255,.72)",
    border:"1px solid rgba(255,255,255,.9)",
    borderRadius:16,
    boxShadow:"0 4px 16px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)",
  };

  return (
    <div style={{
      position:"fixed",inset:0,zIndex:500,
      background:"#e6eaf4",
      display:"flex",flexDirection:"column",
      fontFamily:"var(--f)",
      animation:"pageFadeIn .2s ease",
    }}>
      {/* ── TOP NAV ── */}
      <div style={{
        display:"flex",alignItems:"center",
        background:"rgba(255,255,255,.72)",
        backdropFilter:"blur(40px)",
        borderBottom:"1px solid rgba(255,255,255,.85)",
        boxShadow:"0 2px 16px rgba(0,0,0,.06)",
        flexShrink:0,position:"sticky",top:0,zIndex:10,
      }}>
        <button onClick={onClose} style={{
          display:"flex",alignItems:"center",gap:8,
          padding:"16px 22px",border:"none",
          background:"transparent",cursor:"pointer",
          color:"#64748b",fontSize:13,fontFamily:"var(--f)",
          borderRight:"1px solid rgba(0,0,0,.06)",transition:"all .15s",flexShrink:0,
        }}
          onMouseEnter={e=>e.currentTarget.style.color="#0f172a"}
          onMouseLeave={e=>e.currentTarget.style.color="#64748b"}>
          <span style={{fontSize:16}}>←</span> Members
        </button>

        <div style={{display:"flex",alignItems:"center",gap:13,padding:"10px 22px",flex:1}}>
          {member.photo ? (
            <img src={member.photo} alt={member.name} style={{width:38,height:38,borderRadius:11,objectFit:"cover",flexShrink:0}}/>
          ) : (
            <div style={{width:38,height:38,borderRadius:11,background:member.grad||"linear-gradient(150deg,#64748b,#059669)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,color:"#fff",flexShrink:0,boxShadow:"0 3px 10px rgba(0,0,0,.18)"}}>{member.name ? member.name.slice(0,2).toUpperCase() : "??"}</div>
          )}
          <div>
            <div style={{fontSize:14,fontWeight:700,color:"#0f172a"}}>{member.name}</div>
            <div style={{fontSize:11,color:"#94a3b8",marginTop:1}}>{memberLeads.length} leads generated</div>
          </div>
        </div>

        {/* Filter pills */}
        <div style={{display:"flex",gap:4,background:"rgba(0,0,0,.04)",padding:4,borderRadius:10,marginRight:14}}>
          {["all","new","contacted","qualified","closed"].map(s=>(
            <button key={s} onClick={()=>setFilterSt(s)} style={{
              padding:"5px 13px",borderRadius:7,border:"none",cursor:"pointer",
              fontSize:11.5,fontWeight:filterSt===s?680:450,fontFamily:"var(--f)",
              background:filterSt===s?"rgba(255,255,255,.96)":"transparent",
              color:filterSt===s?"#0f172a":"#94a3b8",
              boxShadow:filterSt===s?"0 2px 8px rgba(0,0,0,.07)":"none",
              transition:"all .15s",textTransform:"capitalize",
            }}>{s}</button>
          ))}
        </div>

        {/* Search */}
        <div style={{position:"relative",marginRight:12}}>
          <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"#94a3b8",pointerEvents:"none"}}>🔍</span>
          <input placeholder="Search leads…" value={search} onChange={e=>setSearch(e.target.value)} style={{
            width:200,padding:"8px 12px 8px 30px",
            background:"rgba(255,255,255,.8)",border:"1px solid rgba(0,0,0,.08)",
            borderRadius:9,color:"#0f172a",fontSize:12,
            fontFamily:"var(--f)",outline:"none",
          }}/>
        </div>

        <button onClick={downloadCSV} style={{
          display:"flex",alignItems:"center",gap:7,padding:"8px 18px",marginRight:20,
          borderRadius:10,border:"1px solid rgba(0,0,0,.1)",
          background:"rgba(255,255,255,.8)",cursor:"pointer",
          fontSize:12,fontWeight:650,color:"#475569",fontFamily:"var(--f)",
          transition:"all .15s",flexShrink:0,
          boxShadow:"0 2px 8px rgba(0,0,0,.06)",
        }}
          onMouseEnter={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color="#0f172a";}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,.8)";e.currentTarget.style.color="#475569";}}>
          ⬇ Export CSV
        </button>
      </div>

      {/* ── STAT BAR ── */}
      <div style={{
        display:"flex",gap:0,
        background:"rgba(255,255,255,.55)",
        borderBottom:"1px solid rgba(255,255,255,.8)",
        flexShrink:0,
      }}>
        {[
          {ico:"🎯",lbl:"Total Leads",  val:memberLeads.length, c:"#0891b2"},
          {ico:"⭐",lbl:"Qualified",    val:totalQual,           c:"#059669"},
          {ico:"✦", lbl:"Avg AI Score", val:avgScore,            c:"#7c3aed"},
          {ico:"📅",lbl:"This Month",   val:memberLeads.filter(l=>l.date.includes("Mar")).length, c:"#d97706"},
          {ico:"📊",lbl:"Showing",      val:filtered.length+" results", c:"#64748b"},
        ].map(({ico,lbl,val,c},i)=>(
          <div key={lbl} style={{
            flex:1,padding:"13px 20px",
            borderRight:i<4?"1px solid rgba(0,0,0,.05)":"none",
            display:"flex",alignItems:"center",gap:12,
          }}>
            <div style={{width:32,height:32,borderRadius:9,background:`${c}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{ico}</div>
            <div>
              <div style={{fontSize:18,fontWeight:780,color:c,letterSpacing:"-.5px",lineHeight:1}}>{val}</div>
              <div style={{fontSize:10.5,color:"#94a3b8",marginTop:3,fontWeight:550}}>{lbl}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── LEAD CARDS GRID ── */}
      <div style={{flex:1,overflowY:"auto",padding:"24px 32px"}}>
        {filtered.length===0?(
          <div style={{textAlign:"center",padding:"80px 0"}}>
            <div style={{fontSize:44,marginBottom:14}}>🎯</div>
            <div style={{fontSize:17,fontWeight:700,color:"#64748b"}}>No leads found</div>
            <div style={{fontSize:13,color:"#94a3b8",marginTop:8}}>Try adjusting your search or filter</div>
          </div>
        ):(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(360px,1fr))",gap:14}}>
            {filtered.map((l,idx)=>{
              const sc  = SC[l.status]||"#64748b";
              const sbg = SBG[l.status]||"rgba(100,116,139,.1)";
              const isExp = expanded===l.id;
              return (
                <div key={l.id}
                  onClick={()=>setExpanded(isExp?null:l.id)}
                  style={{
                    ...cardSt,
                    borderColor:isExp?sc+"44":"rgba(255,255,255,.9)",
                    cursor:"pointer",transition:"all .2s",
                    boxShadow:isExp?`0 8px 28px ${sc}20,inset 0 1px 0 rgba(255,255,255,.95)`:"0 4px 16px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)",
                    overflow:"hidden",
                  }}
                  onMouseEnter={e=>{if(!isExp){e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,.1),inset 0 1px 0 rgba(255,255,255,.95)";}}}
                  onMouseLeave={e=>{if(!isExp){e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)";}}}
                >
                  {/* Top color stripe */}
                  <div style={{height:3,background:`linear-gradient(90deg,${sc},${sc}44)`}}/>

                  {/* Card header */}
                  <div style={{display:"flex",alignItems:"center",gap:14,padding:"16px 18px 12px"}}>
                    <div style={{
                      width:44,height:44,borderRadius:13,flexShrink:0,
                      background:`linear-gradient(135deg,${sc},${sc}88)`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:14,fontWeight:800,color:"#fff",
                      boxShadow:`0 4px 12px ${sc}33`,
                    }}>{l.name.split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:720,color:"#0f172a",letterSpacing:"-.2px"}}>{l.name}</div>
                      <div style={{fontSize:11.5,color:"#94a3b8",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.company}</div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5,flexShrink:0}}>
                      <span style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:sbg,color:sc,textTransform:"capitalize",border:`1px solid ${sc}22`}}>{l.status}</span>
                      <span style={{fontSize:10.5,fontWeight:700,color:"#7c3aed"}}>✦ {l.aiScore}</span>
                    </div>
                  </div>

                  {/* Info row */}
                  <div style={{display:"flex",padding:"0 18px 14px",gap:12,flexWrap:"wrap"}}>
                    {[
                      {ico:"📧",val:l.email},
                      {ico:"🏙",val:`${l.city}`},
                      {ico:"📅",val:l.date},
                    ].map(({ico,val},i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:5,minWidth:0}}>
                        <span style={{fontSize:11,flexShrink:0}}>{ico}</span>
                        <span style={{fontSize:11,color:"#64748b",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{val}</span>
                      </div>
                    ))}
                    <div style={{marginLeft:"auto",fontSize:12,color:"#cbd5e1",transition:"transform .2s",transform:isExp?"rotate(90deg)":"rotate(0deg)",flexShrink:0}}>›</div>
                  </div>

                  {/* Expanded */}
                  {isExp&&(
                    <div style={{padding:"14px 18px 18px",borderTop:"1px solid rgba(0,0,0,.06)",background:"rgba(248,250,252,.6)"}}>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:9,marginBottom:13}}>
                        {[
                          {ico:"📞",lbl:"Phone",   val:l.phone},
                          {ico:"🌍",lbl:"Country", val:l.country},
                          {ico:"🏢",lbl:"Company", val:l.company},
                        ].map(({ico,lbl,val})=>(
                          <div key={lbl} style={{padding:"10px 11px",borderRadius:10,background:"rgba(255,255,255,.9)",border:"1px solid rgba(0,0,0,.06)"}}>
                            <div style={{fontSize:9.5,color:"#94a3b8",fontWeight:700,textTransform:"uppercase",letterSpacing:".07em",marginBottom:4}}>{ico} {lbl}</div>
                            <div style={{fontSize:11.5,color:"#0f172a",fontWeight:570,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{val}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{display:"flex",gap:8}} onClick={e=>e.stopPropagation()}>
                        <button className="btn bp" style={{flex:1,justifyContent:"center",fontSize:12,gap:5}}>📧 Email</button>
                        <button className="btn bs" style={{flex:1,justifyContent:"center",fontSize:12,gap:5}}>📝 Note</button>
                        <button className="btn bs" style={{padding:"0 13px",fontSize:12,justifyContent:"center"}} onClick={downloadCSV}>⬇</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════
   PAGE USERS
═══════════════════════════════════════════ */
